import { createMpsCategoryAccessor, MpsCategory } from '../domain/category/accessor';
import {
  buildCategoryIndex,
  filterByLevelAndParent,
  findCategoriesByLevel,
  findParentCategory,
  flattenCategoriesRecursively,
} from '../domain/category/util';
import { fx } from '@fxts/core';
import { ICategory } from '../domain/category/types.ts';

/**
 * 카테고리 메모리 내 검색 전용 서비스
 * 주어진 MpsCategory 배열에서 원하는 카테고리를 찾는 순수 함수들을 제공
 */
export class CategoryServiceS {
  /**
   * ICategory 데이터를 MpsCategory value-object 로 변환
   */
  static createMpsCategoryAccessor(data: ICategory): MpsCategory {
    return createMpsCategoryAccessor(data);
  }

  /** 주어진 카테고리 배열에서 특정 코드의 카테고리를 검색 */
  static findCategoryByCode(mpsCategories: MpsCategory[], code: string): MpsCategory | null {
    return (
      fx(flattenCategoriesRecursively(mpsCategories)).find((category) => category.getCode() === code) || null
    );
  }

  /** 주어진 카테고리 데이터에서 Level 1 카테고리들 찾기 */
  static findLevel1Categories(mpsCategories: readonly MpsCategory[]): MpsCategory[] {
    return findCategoriesByLevel([...mpsCategories], 1);
  }

  /** 주어진 카테고리 데이터에서 특정 Level 1의 Level 2 하위 카테고리들 찾기 */
  static findLevel2Categories(mpsCategories: readonly MpsCategory[], root_code: string): MpsCategory[] {
    const index = buildCategoryIndex(mpsCategories);
    const allLevel2 = [...mpsCategories].flatMap((cat) => cat.getChildren());
    return filterByLevelAndParent(index, root_code, 'level2', allLevel2);
  }

  /** 주어진 카테고리 데이터에서 특정 Level 2의 Level 3 하위 카테고리들 찾기 */
  static findLevel3Categories(
    mpsCategories: readonly MpsCategory[],
    level2_code: string,
    root_code: string,
  ): MpsCategory[] {
    // 먼저 해당 root_code의 루트 카테고리를 찾기
    const rootCategory = mpsCategories.find((cat) => cat.getCode() === root_code);

    if (!rootCategory) {
      return [];
    }

    // 해당 루트 카테고리의 Level 2 카테고리들 중에서 level2_code 와 일치하는 것 찾기
    const parentCategory = rootCategory.getChildren().find((l2Cat) => l2Cat.getCode() === level2_code);

    return parentCategory ? parentCategory.getChildren() : [];
  }

  /** 특정 레벨의 카테고리들만 찾기 (1, 2, 3) */
  static findCategoriesByLevel(mpsCategories: MpsCategory[], level: number): MpsCategory[] {
    return findCategoriesByLevel(mpsCategories, level);
  }

  /** 부모 카테고리 찾기 */
  static findParentCategory(mpsCategories: MpsCategory[], childCode: string): MpsCategory | undefined {
    return findParentCategory(mpsCategories, childCode);
  }

  /** 주어진 카테고리 배열에서 특정 ID와 레벨의 카테고리를 검색 */
  static findCategoryByIdAndLevel(
    mpsCategories: MpsCategory[],
    id: number,
    level: number,
  ): MpsCategory | undefined {
    return fx(flattenCategoriesRecursively(mpsCategories)).find(
      (category) => category.getId() === id && category.getLevel() === level,
    );
  }
}
